import { useState } from "react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { usePointsByCustomer, usePointsStatement } from "../hooks/usePoints";
import type { PointsLedgerEntry } from "../services/pointsApi";
import { PointsAdjustDialog } from "./PointsAdjustDialog";
import { PointsRedeemDialog } from "./PointsRedeemDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Props {
  customerId: string;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-KE", { dateStyle: "medium", timeStyle: "short" });

const directionStyles = (entry: PointsLedgerEntry) =>
  entry.direction === "Credit" ? "text-emerald-700 font-semibold" : "text-amber-800 font-semibold";

/**
 * Loyalty points panel. Renders the points balance and activity separately from
 * the cash wallet — points and cash are never summed into one figure.
 */
export const PointsBalanceCard = ({ customerId }: Props) => {
  const { hasPermission } = useAuthStore();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [redeemOpen, setRedeemOpen] = useState(false);

  const accountQuery = usePointsByCustomer(customerId);
  const statementQuery = usePointsStatement(customerId, { pageNumber: 1, pageSize: 50 });

  const errorCode = (accountQuery.error as { response?: { data?: { code?: string } } } | undefined)
    ?.response?.data?.code;
  if (errorCode === "Points.AnonymousPool") {
    return (
      <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
        Loyalty points are not available for the shared anonymous walk-in record.
      </div>
    );
  }

  if (accountQuery.isLoading) {
    return <div className="text-sm text-muted-foreground p-4">Loading points…</div>;
  }

  const account = accountQuery.data;
  if (!account) return null;

  const canAdjust = hasPermission("loyalty.adjust");
  const canRedeem = hasPermission("loyalty.redeem");

  return (
    <div className="space-y-4 border-t pt-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Points balance</p>
          <p className="text-3xl font-bold text-indigo-700">
            {account.pointsBalance.toLocaleString("en-KE")} Points
          </p>
          {account.pendingPoints !== 0 && (
            <p className="text-xs text-muted-foreground">
              {account.pendingPoints > 0 ? "+" : ""}
              {account.pendingPoints.toLocaleString("en-KE")} pending approval
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {canRedeem && account.pointsBalance > 0 && (
            <Button onClick={() => setRedeemOpen(true)} variant="default">
              Redeem
            </Button>
          )}
          {canAdjust && (
            <Button onClick={() => setAdjustOpen(true)} variant="outline">
              Adjust points
            </Button>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Points activity</p>
        {statementQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading activity…</div>
        ) : !statementQuery.data || statementQuery.data.length === 0 ? (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            No approved points activity yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Cause</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Points</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statementQuery.data.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{formatDate(e.createdAt)}</TableCell>
                  <TableCell>{e.type}</TableCell>
                  <TableCell className="max-w-xs truncate" title={e.cause ?? undefined}>
                    {e.cause}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {e.reference ?? e.id.slice(0, 8).toUpperCase()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={e.status === "Reversed" ? "destructive" : "secondary"}>{e.status}</Badge>
                  </TableCell>
                  <TableCell className={`text-right ${directionStyles(e)}`}>
                    {e.direction === "Credit" ? "+" : "−"} {e.points.toLocaleString("en-KE")}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PointsAdjustDialog customerId={customerId} open={adjustOpen} onOpenChange={setAdjustOpen} />
      <PointsRedeemDialog customerId={customerId} open={redeemOpen} onOpenChange={setRedeemOpen} />
    </div>
  );
};
